import React, {
  useState, useEffect, useContext, useRef,
} from 'react';
import StoryblokClient, { ISbStoryData } from 'storyblok-js-client';
import { SbEditableContent } from 'storyblok-react';
import { PageStateContext, StoryblokService } from '../../services';
import { Props } from '../types';
import { Teaser, TeaserFields } from './teaser';
import { getTeaserMapping, TeaserPageTypes } from '../../services/compMapping/teasers';

interface DidArticlesListProps {
  articles: SbEditableContent;
}

const teaserMappings = (article, teaserSettings, globalSettings): TeaserFields | null => {
  if (!article) return null;
  return getTeaserMapping(TeaserPageTypes.DID)(article, teaserSettings, globalSettings);
};

export const DidArticlesList = (
  { blok, getComponent, slot }: Props<DidArticlesListProps>,
): JSX.Element => {
  const { setPictureCreditsAdditionalData, settings: pageSettings } = useContext(PageStateContext);
  const storyblokJsClient = useRef(null);
  const [articles, setArticles] = useState([]);

  const getResolvedStories = async (uuids: Array<string>): Promise<Array<ISbStoryData>> => {
    if (!uuids.length) {
      return [];
    }
    const { data } = await storyblokJsClient.current?.getStory('', {
      by_uuids: uuids.join(','),
    });
    return data.stories.reduce((accumulator, story: ISbStoryData) => ({
      ...accumulator,
      [story.uuid]: story,
    }), {});
  };

  useEffect(() => {
    storyblokJsClient.current = new StoryblokClient({
      accessToken: StoryblokService.getConfig().options.accessToken as string,
    });
  }, []);

  useEffect(() => {
    const uuidsToResolve = blok.articles.reduce((accumulator, { content }) => [
      ...accumulator,
      ...(Array.isArray(content.categories) ? content.categories : []),
      ...(Array.isArray(content.authors) ? content.authors : []),
    ], []);

    const getNestedRelations = async () => {
      const resolvedStories = await getResolvedStories(uuidsToResolve);
      const resolvedArticles = blok.articles.map((article) => ({
        ...article,
        content: {
          ...article.content,
          ...(article.content.categories?.length ? {
            categories: article.content.categories.reduce((accumulator, category) => {
              if (resolvedStories[category]) {
                accumulator.push(resolvedStories[category]);
              }
              return accumulator;
            }, []),
          } : {}),
          ...(article.content.authors?.length ? {
            authors: article.content.authors.reduce((accumulator, author) => {
              if (resolvedStories[author]) {
                accumulator.push(resolvedStories[author]);
              }
              return accumulator;
            }, []),
          } : {}),
        },
      }));

      const teaserComponents = resolvedArticles.map(
        (article) => teaserMappings(article, {}, pageSettings),
      );
      setArticles(teaserComponents);
      setPictureCreditsAdditionalData([...teaserComponents]);
    };

    getNestedRelations();
  }, [blok.articles]);

  return (
    <roche-teaser-list slot={slot}>
      <roche-columns center-children={'false'} multiple-rows={true}>
        {articles?.map((article) => (
          // eslint-disable-next-line no-underscore-dangle
          <roche-column key={article._uid} span={4}>
            <Teaser blok={article} getComponent={getComponent} />
          </roche-column>
        ))}
      </roche-columns>
    </roche-teaser-list>
  );
};
